import React from "react";
import SEO from "components/SEO/SEO";
import {
    CheckCircleIcon,
    CheckIcon,
    CreditCardIcon,
    UserIcon,
    QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { FiFileText, FiMessageCircle, FiSearch } from "react-icons/fi";
import { FaDollarSign } from "react-icons/fa";

const Tenants = () => {
    return (
        <div className="min-h-screen  transition-colors duration-300">
            <SEO
              title="For Students — Find Your Room in Lima, Peru"
              description="Everything students and Erasmus need to find and book accommodation in Lima, Peru. Verified properties, simple booking, and trusted landlords on Nomadroof."
              canonical="/tenants"
            />
            {/* Hero Section */}
            <section className="relative bg-gradient-to-r from-orange-500 to-orange-500  py-20 px-4">
                <div className="max-w-7xl mx-auto text-center">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Find Your Perfect Stay
                    </h1>
                    <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
                        Discover thousands of unique properties worldwide and book with
                        confidence
                    </p>
                    <button className="bg-white text-orange-600 px-8 py-3 rounded-full font-medium hover:bg-gray-100 transition duration-300">
                        Start Searching
                    </button>
                </div>
            </section>

            {/* Search Section */}
            <section className="py-16 px-4">
                <div className="max-w-7xl mx-auto rounded-xl shadow-lg p-8 -mt-10 relative z-10">
                    <div className="flex flex-col items-center text-center mb-12 [nc-box-has-hover] [nc-dark-box-bg-has-hover]
">
                        <div className="bg-orange-500 p-4 rounded-full mb-6 shadow-md">
                            <FiSearch className="h-10 w-10 text-white" />
                        </div>
                        <h2 className="text-3xl font-bold mb-4 dark:text-white">
                            Sign Up and Search for your Favorite Roof
                        </h2>
                        <p className="text-gray-600 dark:text-gray-300 max-w-3xl">
                            Sign up, search among the different types of rooms and apartments,
                            and find the property that better fits your requirements.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="nc-CardAuthorBox relative flex flex-col items-center justify-center text-center px-3 py-5 sm:px-6 sm:py-7  [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover">
                            <div className="flex flex-col items-center text-center">
                                <div className="bg-orange-500 p-3 rounded-full mr-4 flex-shrink-0 mb-2">
                                    <CheckCircleIcon className="h-6 w-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg mb-2 dark:text-white">
                                        How do I Sign up?
                                    </h3>
                                    <p className="text-gray-600 dark:text-gray-300">
                                        You can navigate through our website freely, but in order to
                                        contact a Landlord and book a property, you must sign up. So
                                        let's start by clicking on the + Sign Up tab located in the
                                        upper right side of the website.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="nc-CardAuthorBox relative flex flex-col items-center justify-center text-center px-3 py-5 sm:px-6 sm:py-7  [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover">
                            <div className="flex flex-col items-center text-center">
                                <div className="bg-orange-500 p-3 rounded-full mr-4 flex-shrink-0 mb-2">
                                    <FaDollarSign className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg mb-2 dark:text-white">
                                        How do I search?
                                    </h3>
                                    <p className="text-gray-600 dark:text-gray-300">
                                        Type the city you want to go, select the time of your stay,
                                        choose the number of tenants, and click on Search. The next
                                        page will show you a listing of different properties.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Guidance Section */}
            <section className="py-16 px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col items-center text-center mb-12">
                        <div className="bg-orange-500 p-4 rounded-full mb-6 shadow-md">
                            <FiFileText className="h-10 w-10 text-white" />
                        </div>
                        <h2 className="text-3xl font-bold mb-4 dark:text-white">
                            Ask for guidance and Choose your Roof
                        </h2>
                        <p className="text-gray-600 dark:text-gray-300 max-w-3xl">
                            We guide you through the different listings that we have so you
                            can get the option that fits your needs. Send us an email or chat
                            with us!
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="nc-CardAuthorBox relative flex flex-col items-center justify-center text-center px-3 py-5 sm:px-6 sm:py-7  [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover">
                            <div className="bg-orange-500 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                                <UserIcon className="h-6 w-6 text-white" />
                            </div>
                            <h3 className="font-bold text-lg mb-3 text-center dark:text-white">
                                Can I see who will be my Host?
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                Yes, you can see a Host Profile within the listing page by
                                scrolling down and clicking on "See Host Profile". Once in the
                                host page, you will see a picture of the host, a brief
                                description of himself/herself.
                            </p>
                        </div>

                        <div className="nc-CardAuthorBox relative flex flex-col items-center justify-center text-center px-3 py-5 sm:px-6 sm:py-7  [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover">
                            <div className="bg-orange-500  p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                                <FiFileText className="h-6 w-6 text-white" />
                            </div>
                            <h3 className="font-bold text-lg mb-3 text-center dark:text-white">
                                Can I message Host?
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                You can message Host and see its contact information once a
                                booking is confirmed. For privacy policies you are not able to
                                message Host before, however we have a Live Chat Center.
                            </p>
                        </div>

                        <div className="nc-CardAuthorBox relative flex flex-col items-center justify-center text-center px-3 py-5 sm:px-6 sm:py-7  [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover">
                            <div className="bg-orange-500 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                                <CheckIcon className="h-6 w-6 text-white" />
                            </div>
                            <h3 className="font-bold text-lg mb-3 text-center dark:text-white">
                                How can I contact Host after booking?
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                The contact information of Host will be visible to you once
                                booking is confirmed. Click on Host's profile in the listing or
                                look for his/her name in the invoice.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Booking Section */}
            <section className="py-16 px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col items-center text-center mb-12">
                        <div className="bg-orange-500 p-4 rounded-full mb-6 shadow-md">
                            <CreditCardIcon className="h-10 w-10 text-white" />
                        </div>
                        <h2 className="text-3xl font-bold mb-4 dark:text-white">
                            Book and Pay Safely the Roof You Want
                        </h2>
                        <p className="text-gray-600 dark:text-gray-300 max-w-3xl">
                            Once you have decided for the property you want, book the place,
                            pay the invoice and secure your stay!
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div className="nc-CardAuthorBox relative flex flex-col items-center justify-center text-center px-3 py-5 sm:px-6 sm:py-7  [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover">
                            <div className="bg-orange-500 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                                <CheckCircleIcon className="h-6 w-6 text-white" />
                            </div>
                            <h3 className="font-bold text-lg mb-3 text-center dark:text-white">
                                Can I book safely?
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                We guarantee that you will move in to the exact place you are
                                booking. We first verify a property for certainty and quality
                                standards to approve a Host's listing.
                            </p>
                        </div>

                        <div className="nc-CardAuthorBox relative flex flex-col items-center justify-center text-center px-3 py-5 sm:px-6 sm:py-7  [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover">
                            <div className="bg-orange-500 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                                <FiMessageCircle className="h-6 w-6 text-white" />
                            </div>
                            <h3 className="font-bold text-lg mb-3 text-center dark:text-white">
                                How do I book?
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                Once you've checked the listing's details and the photos, click
                                on "Booking Request". Host will have 48 hours to respond to your
                                request.
                            </p>
                        </div>

                        <div className="nc-CardAuthorBox relative flex flex-col items-center justify-center text-center px-3 py-5 sm:px-6 sm:py-7  [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover">
                            <div className="bg-orange-500 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                                <FaDollarSign className="h-5 w-5 text-white" />
                            </div>
                            <h3 className="font-bold text-lg mb-3 text-center dark:text-white">
                                How do I pay my booking?
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                When Host accepts your booking request, you will be sent an
                                Invoice. Go to "My Reservations" in your Dashboard and make the
                                payment.
                            </p>
                        </div>

                        <div className="nc-CardAuthorBox relative flex flex-col items-center justify-center text-center px-3 py-5 sm:px-6 sm:py-7  [ nc-box-has-hover ] [ nc-dark-box-bg-has-hover">
                            <div className="bg-orange-500 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4 mx-auto">
                                <QuestionMarkCircleIcon className="h-6 w-6 text-white" />
                            </div>
                            <h3 className="font-bold text-lg mb-3 text-center dark:text-white">
                                What do I pay to Nomadroof?
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300 text-sm">
                                The first month's rent and the one-time booking fee. We charge
                                this fee in order to keep our platform operational and secure.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
            <section className="py-16 px-4 bg-orange-500">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl font-bold mb-6 text-white">
                        Ready to find your perfect stay?
                    </h2>
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <button className="bg-black text-white px-8 py-3 rounded-full font-medium hover:bg-gray-800 transition duration-300">
                            Browse Properties
                        </button>
                        <button className="bg-white text-orange-500 px-8 py-3 rounded-full font-medium hover:bg-gray-100 transition duration-300">
                            Contact Support
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Tenants;
